/**
 * Mobile vs desktop detection for X9 PWAs.
 *
 * PWAs are camera/touch-first; desktop browsers get a QR handoff instead.
 * Detection uses UA hints first, then touch + viewport heuristics (iPad
 * desktop mode, narrow touch laptops).
 */

export const ALLOW_DESKTOP_SESSION_KEY = 'x9_allow_desktop';
export const ALLOW_DESKTOP_QUERY = 'allowDesktop';

const MOBILE_PHONE_UA =
  /Android.*Mobile|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i;
const MOBILE_TABLET_UA = /iPad|Android(?!.*Mobile)|Tablet|PlayBook|Silk/i;

/** Inputs for unit tests and SSR-safe evaluation. */
export interface MobileDetectionInput {
  maxTouchPoints: number;
  innerWidth: number;
  innerHeight: number;
  userAgent: string;
  hasTouchStart: boolean;
  userAgentDataMobile?: boolean;
}

/** Viewport max edge above which we treat non-mobile-UA clients as desktop. */
export const MOBILE_VIEWPORT_MAX_PX = 768;

export function detectMobileDevice(input: MobileDetectionInput): boolean {
  if (input.userAgentDataMobile === true) return true;

  if (MOBILE_PHONE_UA.test(input.userAgent)) return true;
  if (MOBILE_TABLET_UA.test(input.userAgent)) return true;

  // iPadOS 13+ can report as Macintosh; touch + tablet-sized screen ⇒ mobile.
  if (/Macintosh/i.test(input.userAgent) && input.maxTouchPoints > 1) {
    return true;
  }

  const touchCapable = input.maxTouchPoints > 0 || input.hasTouchStart;
  const maxEdge = Math.max(input.innerWidth, input.innerHeight);

  if (touchCapable && maxEdge <= MOBILE_VIEWPORT_MAX_PX) return true;

  return false;
}

export function readMobileDetectionInput(): MobileDetectionInput {
  const nav = navigator as Navigator & { userAgentData?: { mobile?: boolean } };
  return {
    maxTouchPoints: navigator.maxTouchPoints,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    userAgent: navigator.userAgent,
    hasTouchStart: 'ontouchstart' in window,
    userAgentDataMobile: nav.userAgentData?.mobile,
  };
}

/**
 * QA / local dev bypass. `?allowDesktop=1` persists for the tab session;
 * `VITE_ALLOW_DESKTOP=1` enables bypass for the whole dev build.
 */
export function shouldBypassDesktopHandoff(): boolean {
  if (import.meta.env.VITE_ALLOW_DESKTOP === '1') return true;
  if (typeof window === 'undefined') return false;

  const params = new URLSearchParams(window.location.search);
  if (params.get(ALLOW_DESKTOP_QUERY) === '1') {
    try {
      sessionStorage.setItem(ALLOW_DESKTOP_SESSION_KEY, '1');
    } catch {
      // Private mode / blocked storage — query param still applies this load.
    }
    return true;
  }

  try {
    return sessionStorage.getItem(ALLOW_DESKTOP_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/** URL encoded in the desktop handoff QR (no QA bypass param). */
export function handoffTargetUrl(href: string = window.location.href): string {
  const url = new URL(href);
  url.searchParams.delete(ALLOW_DESKTOP_QUERY);
  return url.toString();
}

export function isDesktopHandoffRequired(): boolean {
  if (shouldBypassDesktopHandoff()) return false;
  if (typeof window === 'undefined') return false;
  return !detectMobileDevice(readMobileDetectionInput());
}
